import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { ArrowRight, Info, Star, Wallet } from 'lucide-react';

interface FeaturedDeposit {
  id: string;
  bank_id: string;
  bank_name: string;
  bank_logo: string;
  product_name: string;
  deposit_type: string;
  description: string;
  interest_rate: string;
  min_amount: string;
  min_term: string;
  features: string[];
  campaign_title?: string;
  campaign_description?: string;
  redirect_url: string;
  is_featured: boolean;
  is_active: boolean;
}

const FeaturedDeposits = () => {
  const [featuredDeposits, setFeaturedDeposits] = useState<FeaturedDeposit[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedDeposits();
  }, []);

  const fetchFeaturedDeposits = async () => {
    try {
      setIsLoading(true);

      const { data, error } = await supabase
        .from('featured_deposits')
        .select('*')
        .eq('is_featured', true)
        .eq('is_active', true)
        .order('display_order', { ascending: true});

      if (error) {
        console.error('Error fetching featured deposits:', error);
        return;
      }

      setFeaturedDeposits(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDepositTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'tl_mevduat': 'TL Mevduat',
      'usd_mevduat': 'USD Mevduat',
      'eur_mevduat': 'EUR Mevduat'
    };
    return types[type] || type;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F7BA45] mx-auto mb-2"></div>
          <p className="text-sm text-slate-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (featuredDeposits.length === 0) {
    return null;
  }

  return (
    <div className="mb-12">
      <div className="flex items-center gap-2 text-[#F7BA45] mb-6">
        <Star className="h-6 w-6" />
        <h2 className="text-2xl font-bold text-slate-800">
          Öne Çıkan Mevduat Fırsatları
        </h2>
      </div>

      <div className="space-y-4">
        {featuredDeposits.map((deposit) => (
          <div
            key={deposit.id}
            className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#F7BA45]/50 transition-all duration-300 hover:shadow-lg"
          >
            <div className="p-6">
              {/* Bank Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <img
                    src={deposit.bank_logo}
                    alt={deposit.bank_name}
                    className="h-12 object-contain"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-semibold text-slate-900">{deposit.product_name}</h3>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F7BA45] text-white text-xs font-medium">
                        Öne Çıkan
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">{deposit.bank_name} • {getDepositTypeLabel(deposit.deposit_type)}</p>
                  </div>
                </div>
              </div>

              {/* Product Description */}
              <p className="text-sm text-slate-700 mb-4">{deposit.description}</p>

              {/* Campaign */}
              {deposit.campaign_title && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="font-medium text-green-800 text-sm">{deposit.campaign_title}</div>
                  {deposit.campaign_description && (
                    <div className="text-xs text-green-700 mt-1">{deposit.campaign_description}</div>
                  )}
                </div>
              )}

              {/* Deposit Details */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-sm text-slate-500 mb-1">Faiz Oranı</div>
                  <div className="text-xl font-bold text-green-600">{deposit.interest_rate}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-sm text-slate-500 mb-1">Min. Tutar</div>
                  <div className="text-lg font-semibold text-slate-800">{deposit.min_amount}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-sm text-slate-500 mb-1">Min. Vade</div>
                  <div className="text-lg font-semibold text-slate-800">{deposit.min_term} Gün</div>
                </div>
              </div>

              {/* Features */}
              {deposit.features && deposit.features.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {deposit.features.map((feature, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Section */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Info size={16} className="text-[#F7BA45]" />
                  <span>Başvuru için {deposit.bank_name}'a yönlendirilirsiniz</span>
                </div>
                <a
                  id={`deposit-apply-${deposit.bank_id}`}
                  data-bank-name={deposit.bank_name}
                  data-product-name={deposit.product_name}
                  href={deposit.redirect_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 px-6 py-2.5 rounded-lg transition-all duration-300 flex items-center gap-2 shadow-md hover:shadow-lg font-medium"
                >
                  BAŞVUR
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeaturedDeposits;
